import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';

import { CloseIcon } from './Styles/Icons';
import { Dialog, DialogActions, DialogTitle, DialogContent, InputGroup, ButtonGroup, Button, CircularButton, FlexRow, CheckboxGroup, ButtonWithLoader } from './Styles/DownloadInventoryItems';

import { downloadInventoryData } from '../../actions/inventoryActionsV2';
import { getRestrictedFields, isSpecialMarketRequired, isSubSubCategoryRequired, isGLCodeRequired, hideDSOInfo, isTaxableRequired } from '../../commons/utils';

const DownloadInventoryItems = ({ open, options, downloadInventoryData, onTrue, onFalse }) => {
	const [downloadingFile, setDownloadingFile] = useState(false);
	const [selectAll, setSelectAll] = useState(false);
	const [selectedFields, setSelectedFields] = useState([]);
	const [error, setError] = useState(null);

	const onTrueHandler = async () => {
		setDownloadingFile(true);
		let errorState = {};
		setError(errorState);

		if (selectedFields.length === 0) {
			errorState['selectedFields'] = 'No fields selected.';
		}

		if (Object.keys(errorState).length) {
			setError(errorState);
		} else {
			let fields = ['supplier_product_id'];

			options.map((option) => {
				if (selectedFields.includes(option.value)) fields.push(option.value);
			});

			await downloadInventoryData('download-inventory-items', { fields: fields });
			await onTrue();
		}

		setDownloadingFile(false);
	};

	const onFalseHandler = async () => {
		await onFalse();
	};

	useEffect(() => {
		setSelectAll(false);
		setError(null);
	}, [open]);

	useEffect(() => {
		if (selectAll) {
			setSelectedFields(options.map((option) => option.value));
		} else {
			setSelectedFields([]);
		}
	}, [selectAll]);

	return (
		<Dialog
			maxWidth='sm'
			fullWidth
			open={open}
			onClose={() => (downloadingFile ? null : onFalseHandler())}
		>
			<DialogTitle>
				Download Inventory Items
				<ButtonGroup alignRight>
					<CircularButton
						error
						outline
						disabled={downloadingFile}
						onClick={() => onFalseHandler()}
					>
						<CloseIcon />
					</CircularButton>
				</ButtonGroup>
			</DialogTitle>

			<DialogContent style={{ marginTop: '-30px' }}>
				<FlexRow style={{ padding: '0 1rem' }}>Select the fields which you want to include in the data sheet. Item ID will be available by default.</FlexRow>

				<FlexRow style={{ padding: '0 1rem' }}>
					<InputGroup error={error && error.selectAll}>
						<CheckboxGroup
							name={'select-all'}
							disabled={false}
							value={selectAll ? ['select_all'] : []}
							onClick={(e) => {
								e.target.value.includes('select_all') ? setSelectAll(true) : setSelectAll(false);
							}}
							error={error && error.selectAll}
							options={[{ name: 'Select All', value: 'select_all', disabled: false }]}
						/>
					</InputGroup>
				</FlexRow>

				<FlexRow style={{ padding: '0 1rem' }}>
					<InputGroup
						label='Select Fields'
						markRequired={true}
						error={error && error.selectedFields}
					>
						<CheckboxGroup
							name={'selected-fields'}
							disabled={false}
							value={selectedFields}
							onClick={(e) => {
								setSelectedFields(e.target.value.split(','));
							}}
							error={error && error.selectedFields}
							options={options}
						/>
					</InputGroup>
				</FlexRow>
			</DialogContent>

			<DialogActions style={{ margin: '0 0.5rem' }}>
				<ButtonGroup alignRight>
					<Button
						primary
						outline
						disabled={downloadingFile}
						onClick={(e) => onFalseHandler()}
					>
						Cancel
					</Button>
					<ButtonWithLoader
						primary
						loading={downloadingFile}
						disabled={selectedFields.length === 0}
						onClick={(e) => onTrueHandler()}
					>
						Download
					</ButtonWithLoader>
				</ButtonGroup>
			</DialogActions>
		</Dialog>
	);
};

const mapStatesToProps = (state) => {
	const supplier = state.supplier.supplier;
	const supplierMap = state.supplier.suppliers_map;
	const groupId = state.session.groupId;
	let isLivePriceSupplier = (supplierMap[supplier] || {}).is_price_fetched || false;

	let restrictedFields = getRestrictedFields(state.session, state.supplier);

	let options = [
		{ name: 'Item Name', value: 'name', disabled: false },
		{ name: 'Manufacturer Name', value: 'manufacturer_name', disabled: false },
		{ name: 'Manufacturer SKU', value: 'manufacturer_id', disabled: false },
		{ name: 'Item Desciption', value: 'description', disabled: false },
	];
	options = !isLivePriceSupplier ? [...options, { name: 'Stock Status', value: 'availability_status', disabled: false }] : options;
	options = [
		...options,
		{ name: 'Category', value: 'category', disabled: false },
		{ name: 'Sub Category', value: 'subcategory', disabled: false },
	];
	options = isTaxableRequired(groupId) ? [...options, { name: 'Is Taxable', value: 'taxable', disabled: false }] : options;
	options = isSubSubCategoryRequired(groupId) ? [...options, { name: 'Sub Sub Category', value: 'subsubcategory', disabled: false }] : options;
	options = isGLCodeRequired(groupId) ? [...options, { name: 'GL Code', value: 'gl_code', disabled: false }] : options;
	options = isSpecialMarketRequired(supplier) ? [...options, { name: 'Is Special Market', value: 'is_special_market', disabled: false }] : options;
	options = !(hideDSOInfo(groupId) || restrictedFields.includes('group_ids')) ? [...options, { name: 'Mapped DSOs', value: 'group_ids', disabled: false }] : options;
	options = !(hideDSOInfo(groupId) || restrictedFields.includes('group_private')) ? [...options, { name: 'Keep only for DSOs', value: 'group_private', disabled: false }] : options;
	options = [
		...options,
		{ name: 'UOM', value: 'uom', disabled: false },
		{ name: 'Packaging', value: 'pkg', disabled: false },
		{ name: 'Quantity', value: 'qty', disabled: false },
		{ name: 'Minimum Quantity', value: 'min_qty', disabled: false },
		{ name: 'Maximum Quantity', value: 'max_qty', disabled: false },
		{ name: 'Incremental Quantity', value: 'incr_qty', disabled: false },
		// { name: 'Mapped Supplier', value: 'map_to_supplier', disabled: false },
		// { name: 'Mapped Supplier Product ID', value: 'mapped_supplier_product_id', disabled: false },
		{ name: 'Alternate Text', value: 'search_txt', disabled: false },
	];

	options = !restrictedFields.includes('keywords') ? [...options, { name: 'Keywords', value: 'keywords', disabled: false }] : options;
	options = !restrictedFields.includes('unspsc') ? [...options, { name: 'UNSPSC', value: 'unspsc', disabled: false }] : options;

	options = [
		...options,
		{ name: 'Product URL', value: 'product_url', disabled: false },
		{ name: 'Attributes', value: 'attrs', disabled: false },
		{ name: 'Image URLs', value: 'image_urls', disabled: false },
	];

	return {
		groupId: groupId,
		supplier: supplier,
		options: options,
	};
};

const mapFunctionsToProps = {
	downloadInventoryData,
};

export default connect(mapStatesToProps, mapFunctionsToProps)(DownloadInventoryItems);
